// «Ждёт вас» — одна очередь и один счётчик (api /api/waiting). Меню, нижняя навигация телефона, индикатор агента,
// «Решения» и «Сводка» читают одни и те же данные из кэша — число везде одинаковое.
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { FeedRun, Proposal } from '../agent-api';
import { api } from '../api';
import type { AttachmentRef } from '../chat/attach';
import { useAgentStatus } from '../streams';

export interface Series {
  days: Array<{ day: string; spend: number; leads: number }>;
  spend: number;
  leads: number;
  cpl: number | null;
}

interface Base {
  key: string;
  /** main — основная очередь; tech — технические разрешения (внизу, свёрнуто). */
  group: 'main' | 'tech';
  money: boolean;
  dueAt: string | null;
  at: string;
}

export interface PermissionItem {
  id: string;
  threadId: string | null;
  threadTitle: string | null;
  toolName: string;
  input: Record<string, unknown>;
  offer: string | null;
  expiresAt: string;
}

export type WaitingItem = Base &
  (
    | { kind: 'proposal' | 'advice'; proposal: Proposal & { series: Series | null } }
    | {
        kind: 'question';
        question: {
          runId: string;
          threadId: string | null;
          label: string;
          headline: string | null;
          questions: string[];
        };
      }
    | {
        kind: 'task';
        task: {
          id: number;
          title: string;
          progress: string | null;
          /** Вопросы из итога последней работы по задаче, если агент их задал. */
          questions: string[];
          threadId: string | null;
          createdBy: string;
        };
      }
    | { kind: 'batch'; batch: { id: number; title: string; offer: string; count: number; target: number } }
    | { kind: 'cr'; cr: { id: number; title: string; summary: string | null; requestedBy: string } }
    | { kind: 'held'; held: { count: number; oldestAt: string } }
    | { kind: 'permission'; permission: PermissionItem }
  );

export interface WaitingData {
  total: number;
  items: WaitingItem[];
  /** Режим агента: при полной автономии «Решения» объясняют, почему сюда всё равно что-то приходит. */
  autonomy?: 'analytics_only' | 'propose' | 'autonomous_limited' | 'full';
}

export const WAITING_KEY = ['waiting'] as const;

/**
 * Вопрос агента, на который отвечает владелец: из итога работы или задачи «ждёт вас». Чат, открытый ради ответа,
 * закрепляет его сверху, а первое сообщение уходит ответом на него (api /api/waiting/answer).
 */
export interface Ask {
  /** Строка очереди «Ждёт вас»: question:<runId> или task:<id>. */
  key: string;
  threadId: string;
  /** Что спросил агент. */
  questions: string[];
  /** Откуда вопрос: «Утренний брифинг», «Задача «…»». */
  source: string;
  at: string;
  /** Задача: ответ поставит её продолжение в очередь агента. */
  task?: boolean;
  /** Ответ уже ушёл — плашка говорит это, поле ввода снова обычное. */
  answered?: boolean;
}

/** Вопрос из строки очереди — для чата с закреплённым вопросом. */
export function askOf(i: WaitingItem): Ask | null {
  if (i.kind === 'question' && i.question.threadId)
    return {
      key: i.key,
      threadId: i.question.threadId,
      questions: i.question.questions,
      source: i.question.label,
      at: i.at,
    };
  if (i.kind === 'task' && i.task.threadId)
    return {
      key: i.key,
      threadId: i.task.threadId,
      questions: i.task.questions.length ? i.task.questions : [i.task.progress ?? i.task.title],
      source: `Задача «${i.task.title}»`,
      at: i.at,
      task: true,
    };
  return null;
}

/** Вопросы из итога работы в ленте (и в выводе агента на «Сводке») — для чата с закреплённым вопросом. */
export function askOfRun(r: FeedRun): Ask | null {
  const questions = r.report?.asks_owner ?? [];
  if (!r.threadId || !questions.length) return null;
  return {
    key: `question:${r.id}`,
    threadId: r.threadId,
    questions,
    source: r.kindLabel,
    at: r.endedAt ?? r.startedAt ?? r.createdAt,
  };
}

export interface AnswerResult {
  threadId: string;
  /** Ответ поставил продолжение задачи в очередь (агент возьмётся, когда будет место). */
  queued: boolean;
}

/** Ответ владельца на вопрос агента или задачу: api передаёт его агенту, и тот продолжает работу. */
export function answerAgent(
  key: string,
  text: string,
  attachments: AttachmentRef[] = [],
): Promise<AnswerResult> {
  return api<AnswerResult>('/api/waiting/answer', {
    key,
    text,
    ...(attachments.length ? { attachments } : {}),
  });
}

/** Строка ушла из очереди сразу после ответа — не дожидаясь, пока api пересчитает очередь. */
export function dropWaiting(qc: QueryClient, key: string): void {
  qc.setQueryData<WaitingData>(WAITING_KEY, (d) => {
    if (!d) return d;
    const items = d.items.filter((i) => i.key !== key);
    return { ...d, items, total: items.length };
  });
}

export function useWaiting() {
  return useQuery({
    queryKey: WAITING_KEY,
    queryFn: () => api<WaitingData>('/api/waiting'),
    refetchInterval: 30_000,
  });
}

/** Число «Ждёт вас»; пока очередь не пришла — null (значок не мигает нулём). */
export function useWaitingTotal(): number | null {
  return useWaiting().data?.total ?? null;
}

/** После решения — обновить очередь и всё, что от неё зависит (советы, журнал). */
export function useRefreshWaiting(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: WAITING_KEY });
    void qc.invalidateQueries({ queryKey: ['agent', 'proposals'] });
    void qc.invalidateQueries({ queryKey: ['agent', 'journal'] });
  }, [qc]);
}

/**
 * Статус агента пришёл с другими счётчиками (новый запрос разрешения, решение в Telegram, конец работы с вопросами) —
 * очередь могла измениться: перечитать сразу, не дожидаясь периодического обновления. Вызывается один раз, в каркасе.
 */
export function useWaitingSync(): void {
  const { status } = useAgentStatus();
  const qc = useQueryClient();
  const sig = status
    ? [
        status.pendingPermissions,
        status.openProposals ?? 0,
        status.activeRuns,
        status.working,
        status.queue?.waiting ?? 0,
        (status.alerts ?? []).length,
      ].join('|')
    : '';
  const prev = useRef(sig);
  useEffect(() => {
    if (!sig || sig === prev.current) return;
    prev.current = sig;
    void qc.invalidateQueries({ queryKey: WAITING_KEY });
  }, [sig, qc]);
}
